import React, { createContext, useState, useContext, useEffect, useCallback, useRef, useMemo } from 'react';

// Se mantiene igual
export const UserContext = createContext();

export const UserProvider = ({ children }) => {
   const [userData, setUserData] = useState(null);

   const [isLoading, setIsLoading] = useState(true); // Siempre empieza en true para la carga inicial
   const [isAuthenticated, setIsAuthenticated] = useState(false);

   // Usamos useRef para evitar problemas con variables globales. Es la forma correcta en React.
   const isFetching = useRef(false);

   // Función unificada y mejorada para obtener datos del usuario
   const fetchUserData = useCallback(async () => {
      // Evita llamadas simultáneas
      if (isFetching.current) return null;

      const jwt = localStorage.getItem('jwt');
      const email = localStorage.getItem('userEmail');

      // Si faltan datos esenciales, limpiamos el estado y terminamos
      if (!jwt || !email) {
         setIsAuthenticated(false);
         setUserData(null);
         setIsLoading(false);
         return null;
      }

      isFetching.current = true;
      // No iniciamos isLoading aquí para evitar parpadeos si el usuario ya está navegando

      try {
         const response = await fetch('https://pt-api.digit-a.net/api/users/get-user-by-email', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
         });

         if (response.ok) {
            const data = await response.json();
            console.log('User data fetched successfully:', data);
            setUserData(data);
            setIsAuthenticated(true);
            return data;
         } else {
            // Si el token es inválido o hay otro error, limpiamos todo
            console.error('Failed to fetch user data, logging out.');
            localStorage.removeItem('jwt');
            localStorage.removeItem('userEmail');
            setUserData(null);
            setIsAuthenticated(false);
            return null;
         }
      } catch (error) {
         console.error('Error in fetchUserData:', error);
         setUserData(null);
         setIsAuthenticated(false);
         return null;
      } finally {
         isFetching.current = false;
         setIsLoading(false); // Terminamos de cargar, sea con éxito o con error
      }
   }, []); // useCallback con dependencias vacías para que la función no se recree

   // Efecto de carga inicial, solo se ejecuta una vez cuando la app carga
   useEffect(() => {
      console.log('UserContext mounted. Initializing auth...');
      fetchUserData();
   }, [fetchUserData]);

   // Función de LOGIN refactorizada (el cambio más importante)
   const login = async (email, password) => {
      setIsLoading(true);
      try {
         const response = await fetch('https://pt-api.digit-a.net/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
         });

         const data = await response.json();
         if (!response.ok) {
            return { success: false, message: data.message || 'Invalid credentials' };
         }

         const jwt = data.token || data.payload?.jwt;
         if (jwt) {
            localStorage.setItem('jwt', jwt);
            localStorage.setItem('userEmail', email);

            // ¡CRUCIAL! Después de guardar el token, llamamos a fetchUserData
            // para cargar el estado COMPLETO del usuario ANTES de que termine el login.
            const fetchedUser = await fetchUserData();

            if (fetchedUser) {
               return { success: true };
            } else {
               return { success: false, message: 'Could not fetch user data after login.' };
            }
         }
         return { success: false, message: 'No token received.' };

      } catch (error) {
         console.error('Login API error:', error);
         return { success: false, message: 'Connection error.' };
      } finally {
         setIsLoading(false);
      }
   };

   // Función de LOGOUT refactorizada (más simple)
   const logout = () => {
      localStorage.removeItem('jwt');
      localStorage.removeItem('userEmail');
      setUserData(null);
      setIsAuthenticated(false);
   };

   const userName = userData?.user?.fullName || userData?.name || '';
   const userEmail = userData?.user?.email || '';
   // Usamos useMemo para optimizar y evitar re-renderizados innecesarios en los componentes consumidores
   const value = useMemo(() => ({
      userData,
      userName,
      userEmail,
      isLoading,
      isAuthenticated,
      login,
      logout,
      fetchUserData // Exportamos fetch si lo necesitas para refrescar datos manualmente
   }), [userData, isLoading, isAuthenticated]);

   return (
      <UserContext.Provider value={value}>
         {children}
      </UserContext.Provider>
   );
};
// Se mantiene igual
export const useUser = () => useContext(UserContext);